import { Button, Flex, spaces, Text } from '@electricjs/arc';
import { useTranslation } from 'react-i18next';
import styled, { CSSProperties } from 'styled-components';

type ApprovalButtonsProps = {
  onApprove: () => void;
  onReject: () => void;
  style?: CSSProperties;
};

const Container = styled(Flex)`
  gap: ${spaces.space_xs_rem};
  flex-direction: column;
  align-items: flex-end;
  margin-top: ${spaces.space_m_rem};
  animation: slideIn 0.2s ease-out forwards;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ButtonContent = styled(Flex)`
  gap: ${spaces.space_xs_rem};
  align-items: center;
`;

export const ApprovalButtons = ({
  onApprove,
  onReject,
  style,
}: ApprovalButtonsProps) => {
  const { t } = useTranslation('ai-chat');

  // Detect OS for keyboard shortcuts
  const isMac =
    typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPod|iPad/.test(navigator.platform);
  const yesKeyboardHint = isMac
    ? t('approval.yesKeyboardHintMac')
    : t('approval.yesKeyboardHintOther');
  const noKeyboardHint = isMac
    ? t('approval.noKeyboardHintMac')
    : t('approval.noKeyboardHintOther');

  return (
    <Container style={style}>
      <Button
        id="approval-yes-button"
        testId="approval-yes-button"
        aria-label={t('approval.yesAriaLabel')}
        variant="outline"
        intent="primary"
        onClick={onApprove}
        size="small">
        <ButtonContent>
          <span>{t('approval.yes')}</span>
          <Text variant="legal" intent="disabled">
            {yesKeyboardHint}
          </Text>
        </ButtonContent>
      </Button>
      <Button
        id="approval-no-button"
        testId="approval-no-button"
        aria-label={t('approval.noAriaLabel')}
        variant="outline"
        intent="neutral"
        onClick={onReject}
        size="small">
        <ButtonContent>
          <span>{t('approval.no')}</span>
          <Text variant="legal" intent="disabled">
            {noKeyboardHint}
          </Text>
        </ButtonContent>
      </Button>
    </Container>
  );
};
